<script setup>
import {useForm, Link} from '@inertiajs/vue3';
import TextInput from "@/Components/TextInput.vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import Price from "@/Components/Price.vue";
import InputError from "@/Components/InputError.vue";
import {PhX, PhArrowClockwise} from '@phosphor-icons/vue';
import vueCountryRegionSelect from 'vue3-country-region-select';
import {countryWhitelist, productAsAnalyticItem, eventBus} from '@/mixins';

const {CountrySelect} = vueCountryRegionSelect;
import {inject, onMounted, onUnmounted, ref} from "vue";
import {event} from "vue-gtag";

const props = defineProps({
    cart: Object,
    estimate: Boolean,
    editable: Boolean
});

const route = inject('route');
const discountForm = useForm({
    clear: false,
    code: '',
});

const shippingForm = useForm({
    zip: props.cart.to_address?.zip ?? '',
    country: props.cart.to_address?.country ?? 'US',
});

const showShippingForm = ref(shippingForm.errors.zip);
const showShippingCountry = ref(shippingForm.errors.country || shippingForm.country !== 'US');

function submitDiscount() {
    discountForm.post(route('cart.discount'), {
        preserveScroll: true,
        onSuccess: () => {
            discountForm.clear = false;
            if (props.cart.coupon?.code) {
                event("add_coupon_code", {
                    currency: "USD",
                    coupon: props.cart.coupon?.code,
                    value: props.cart.subtotal / 100,
                    items: props.cart.products.map((product, i) => {
                        return {
                            ...productAsAnalyticItem(product, i),
                            quantity: Math.ceil(product.cart.quantity),
                        };
                    }),
                });
            }
        }
    });
}

function submitShipping() {
    shippingForm.post(route('cart.shipping'), {
        preserveScroll: true,
        onSuccess: () => {
            showShippingForm.value = false;
        }
    });
}

function clearCode() {
    discountForm.clear = true;
    submitDiscount();
}

const codeRef = ref();

const focusInput = () => {
    codeRef.value?.focus();
};

onMounted(() => {
    eventBus.on("focusOnCode", focusInput);
});

onUnmounted(() => {
    eventBus.off("focusOnCode", focusInput);
});

</script>
<template>
    <dl class="space-y-6 border-t border-gray-200 pt-6 text-sm font-medium text-gray-90">
        <div class="flex items-center justify-between">
            <dt class="text-gray-600">Subtotal</dt>
            <dd>
                <Price :value="cart.subtotal"/>
            </dd>
        </div>

        <div class="flex items-center justify-between">
            <dt class="text-gray-600">Discount Code<br/><span class="text-xs text-gray-600">or Gift Card</span>
                <template v-if="cart.coupon">
                    <span
                        class="m-1 inline-flex items-center rounded-full bg-gray-200 py-1.5 pl-3 pr-2 text-sm font-medium text-gray-900">
                        <span>{{ cart.coupon.code }}</span>
                        <button type="button"
                                @click="clearCode"
                                class="ml-1 inline-flex h-6 w-6 flex-shrink-0 rounded-full p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-500">
                            <span class="sr-only">Remove code {{ cart.coupon.code }}</span>
                            <PhX class="w-full h-full" aria-hidden="true"/>
                        </button>
                    </span>
                    <InputError v-if="cart.coupon.error" class="mt-2" :message="cart.coupon.error"/>
                </template>
            </dt>
            <dd class="text-right">
                <template v-if="cart.coupon">
                    <template v-if="cart.discount < 0">
                        <Price :value="cart.discount"/>
                        <br/>
                    </template>
                    <span class="text-gray-400 font-bold">
                        {{ cart.coupon.description }}
                    </span>
                </template>
                <template v-else>
                    <form @submit.prevent="submitDiscount">
                        <div class="mt-1 ml-2 flex space-x-2">
                            <TextInput
                                id="code"
                                type="text"
                                ref="codeRef"
                                class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                v-model="discountForm.code"
                                placeholder="CODE"
                                spellcheck="false"
                                autocapitalize="none"
                            />
                            <PrimaryButton :disabled="discountForm.processing" class="text-xs">
                                Apply
                            </PrimaryButton>
                        </div>
                        <InputError class="mt-2" :message="discountForm.errors.code"/>
                    </form>
                </template>
            </dd>
        </div>

        <div class="flex items-center justify-between">
            <dt class="text-gray-600">
                <template v-if="estimate">Estimated</template>
                Shipping
                <span v-if="cart.to_address?.zip && !showShippingForm && editable"
                      class="m-1 inline-flex items-center rounded-full bg-gray-200 py-1.5 pl-3 pr-2 text-sm font-medium text-gray-900">
                        <span>{{ cart.to_address.zip }} - {{ cart.to_address.state ?? cart.to_address.country }}</span>
                        <button type="button"
                                @click="showShippingForm = true"
                                class="ml-1 inline-flex h-6 w-6 flex-shrink-0 rounded-full p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-500">
                            <span class="sr-only">Remove zipcode</span>
                            <PhX class="w-full h-full" aria-hidden="true"/>
                        </button>
                    </span>
            </dt>
            <dd>
                <template v-if="cart.giftCardsOnly">
                    Electronic Delivery
                </template>
                <template v-else-if="cart.pickup && !estimate">
                    In-store Pickup
                </template>
                <template v-else-if="cart.to_address?.zip && !showShippingForm">
                    <template v-if="cart.deliveryMethods.length">
                        <Price v-if="cart.shipping > 0" :value="cart.shipping"/>
                        <template v-else>FREE</template>
                    </template>
                    <template v-else>
                        <Link :href="route('cart.recalculate')" class="text-indigo-600">
                            <PhArrowClockwise class="h-4 w-4 inline-block" aria-hidden="true"/>
                            Recalculate
                        </Link>
                    </template>
                </template>
                <template v-else-if="editable">
                    <form @submit.prevent="submitShipping">
                        <div class="mt-1 ml-2 flex space-x-2">
                            <TextInput
                                id="zip"
                                type="text"
                                class="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                v-model="shippingForm.zip"
                                placeholder="Zipcode"
                                autocomplete="shipping postal-code"
                            />
                            <CountrySelect
                                id="country"
                                v-if="showShippingCountry"
                                v-model="shippingForm.country"
                                :country="shippingForm.country"
                                placeholder="Country"
                                :autocomplete="true"
                                :disablePlaceholder="true"
                                :whiteList="countryWhitelist"
                                class="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                topCountry="US"
                            />
                            <PrimaryButton :disabled="shippingForm.processing" class="text-xs">
                                Apply
                            </PrimaryButton>
                        </div>
                        <button
                            type="button"
                            v-if="!showShippingCountry"
                            class="inline-block float-right mt-2 text-sm cursor-pointer text-end"
                            @click="showShippingCountry=true; shippingForm.country = ''">Not in the US?</button>
                        <InputError class="mt-2" :message="shippingForm.errors.zip"/>
                        <InputError class="mt-2" :message="shippingForm.errors.country"/>
                    </form>
                </template>
                <template v-else>
                    Not yet calculated
                </template>
            </dd>
        </div>

        <div v-if="cart.tax" class="flex items-center justify-between">
            <dt class="text-gray-600">
                <template v-if="estimate">Estimated</template>
                Tax
            </dt>
            <dd>
                <Price :value="cart.tax"/>
            </dd>
        </div>

        <div v-if="cart.total > 0 || cart.coupon"
             class="flex items-center justify-between border-t border-gray-200 pt-6">
            <dt class="text-base">
                <template v-if="estimate">Estimated</template>
                Total
            </dt>
            <dd class="text-base">
                <Price :value="cart.total"/>
            </dd>
        </div>
    </dl>
</template>
